import allProducts from '../../data/all-products.json';

const sw = (key) => allProducts.products[key] ?? [];

export const productData = {
  // ── BLINDS ──────────────────────────────────────────────────────────────────
  vertical: {
    id: 'vertical-blind',
    name: 'Vertical Blinds',
    category: 'BLINDS',
    description: 'A versatile, low-profile solution built for wide windows and sliding glass doors.',
    longDescription: 'Vertical blinds offer smooth traversing and precise light management in a design that suits residential living rooms, home offices, and commercial spaces alike.',
    keyFeatures: [
      'Designed for large openings and patio doors',
      'Durable traveler track for long, reliable use',
      'Wide selection of vane widths and configurations',
      'Cordless wand option for child and pet safety',
    ],
    styleOptions: [
      'Vane widths: 2 in. to 7 in.',
      'Bay window, skylight, standard, and curved track layouts',
      'Anodized aluminum or powder-coated white finishes',
      'Single runs up to 20 ft wide',
    ],
    price: 'Contact for quote',
    images: ['/vert_blinds/vertical_blinds_productpage.jpg'],
    operatingSystems: [
      {
        title: 'Motorized',
        details: [
          'Quiet, precise motor operation for effortless daily use',
          'Tilt and traverse via remote, wall switch, or smartphone app',
          'Compatible with Somfy®, Amazon Alexa, and Google Home',
          'Cordless design enhances safety and keeps the look clean',
          'Schedule automation for hands-free light control',
        ],
      },
      {
        title: 'Wand Control (Cordless)',
        details: [
          'Single wand handles both tilt and traverse functions',
          'No exposed cords or chains — safer for households with children and pets',
          'Reliable manual system with minimal maintenance',
          'Ideal for everyday use in high-traffic rooms',
        ],
      },
    ],
    swatches: sw('vertical'),
  },
  aluminum: {
    id: 'aluminum-blind',
    name: 'Aluminum Blinds',
    category: 'BLINDS',
    description: 'A slim, durable blind built for easy upkeep in busy residential and commercial settings.',
    longDescription: 'Constructed from heat-treated aluminum, these blinds resist warping, bending, and fading without sacrificing style. A practical pick for kitchens, offices, and rental properties.',
    keyFeatures: [
      'Heat-treated aluminum resists bending and warping',
      'FR-certified for both residential and commercial applications',
      'Available in 6-gauge and 8-gauge aluminum',
      'Low-maintenance finish that cleans with a wipe',
    ],
    styleOptions: [
      'Slat widths: 1 in. and 2 in.',
      '46 color options in 1 in.; 20 color options in 2 in.',
      'Coordinated valances, tapes, and hardware',
    ],
    price: 'Contact for quote',
    images: ['/alum_blinds/aluminum_blinds_productpage.jpg'],
    operatingSystems: [
      {
        title: 'Cordless',
        details: [
          'Available in both 1" and 2" slat sizes',
          'Smooth cord-free lift with child-safe design',
          'Optional wand tilt for slat angle adjustment',
        ],
      },
      {
        title: 'Standard Cord Lock',
        details: [
          'Traditional corded lift with reliable lock mechanism',
          'Wand tilt available for easy slat control',
          'Well-suited for wider or taller window openings',
        ],
      },
      {
        title: 'Lift & Lock (2 in. Only)',
        details: [
          'Exclusive to 2 in. aluminum blinds',
          'Locks the blind securely at any height with a simple push',
        ],
      },
    ],
    swatches: sw('aluminum'),
  },
  wood: {
    id: 'wood-blind',
    name: 'Wood Blinds',
    category: 'BLINDS',
    description: 'Warm, natural hardwood blinds that bring texture and character to any room.',
    longDescription: 'Each wood blind is crafted from premium hardwood for a rich, refined finish. Smooth slat operation and a wide range of stains and paints make these an ideal fit for traditional and transitional interiors.',
    keyFeatures: [
      'Premium hardwood construction for lasting beauty',
      'Wide range of stains and painted finishes',
      'Adjustable slats for precise light and privacy control',
      'Cordless and motorized lift options available',
    ],
    styleOptions: [
      'Slat widths: 2 in. and 2½ in.',
      'Valance styles: 3½ in. Colonial and 3¼ in. Crown',
      'Classic trapezoid bottom rail',
      'Color-matched tapes and hardware',
    ],
    price: 'Contact for quote',
    images: ['/wood_blinds/wood_blinds_productpage.jpg'],
    operatingSystems: [
      {
        title: 'Lift & Lock Cordless',
        details: [
          'Bottom rail button locks the blind precisely in place',
          'Easy to level and reposition at any height',
        ],
      },
      {
        title: 'Cordless',
        details: [
          'Patented cord-free system in both 2 in. and 2½ in. slat sizes',
          'Fits all headrail sizes',
          'Child- and pet-safe with smooth, reliable operation',
        ],
      },
    ],
    swatches: sw('wood'),
  },
  faux: {
    id: 'faux-blind',
    name: 'Faux Wood Blinds',
    category: 'BLINDS',
    description: 'All the warmth of real wood in a moisture-resistant blind built for demanding spaces.',
    longDescription: 'Faux wood blinds are made from a durable polymer composite that holds up in high-humidity rooms like bathrooms, kitchens, and laundry rooms. They look the part without the upkeep.',
    keyFeatures: [
      'Moisture- and heat-resistant polymer construction',
      'Will not warp, fade, or crack over time',
      'GREENGUARD certified with flame-retardant options',
      'Wipes clean with ease',
    ],
    styleOptions: [
      'Slat widths: 2 in. and 2½ in.',
      'Finish textures: Smooth, embossed, standard print, and woodgrain',
      'Valance styles: 3 in. Modern, 3 in. Crescent, and 3 in. Decorative',
      'Bottom rail: Trapezoid or Lift & Lock™ cordless',
    ],
    price: 'Contact for quote',
    images: ['/faux_blinds/faux_blinds_productpage.jpg'],
    operatingSystems: [
      {
        title: 'Lift & Lock Cordless',
        details: [
          'Bottom rail button for precise height adjustment and easy leveling',
          'Cord-free design keeps the look clean and uncluttered',
        ],
      },
      {
        title: 'Cordless',
        details: [
          'Available in both 2 in. and 2½ in. slat sizes',
          'Patented system with smooth, reliable cord-free lift',
          'Child- and pet-safe',
        ],
      },
    ],
    swatches: sw('faux'),
  },

  // ── SHUTTERS ────────────────────────────────────────────────────────────────
  plantation: {
    id: 'plantation-shutter',
    name: 'Plantation Shutters',
    category: 'SHUTTERS',
    description: 'Classic hardwood shutters that deliver timeless style and exceptional light control.',
    longDescription: 'Custom-built to fit your windows exactly, our plantation shutters combine solid hardwood construction with a smooth louver system and a wide range of finish options — a lasting investment in your home.',
    keyFeatures: [
      'Solid hardwood with natural grain and texture',
      'Custom paint and stain finishes available',
      'Multiple louver sizes for varying light and privacy needs',
      'Hidden tilt option for a cleaner look',
      'Superior insulation properties',
    ],
    styleOptions: [
      'Louver sizes: 2½ in., 3½ in., and 4½ in.',
      'Frame styles: L-Frame, Z-Frame, Deco/Trim, LZ/Deep LZ, and Hanging Strip',
      'Panel configurations: Single, double, bi-fold, and bypass',
      'Tilt types: Center, Hidden, Split, and Invisible',
      'Mount options: Inside and outside mount',
      'Specialty shapes: Arches, angles, circles, and more',
      'French door cutouts available',
    ],
    price: 'Contact for quote',
    images: ['/plantation_shutters/plantation_shutters_productpage.jpg'],
    tiltOptionsImage: {
      imageUrl: '/plantation_shutters/Wood-Shutters-Tilt-Options.png',
      imageAlt: 'Plantation shutters tilt options chart',
    },
    specialityOptionsImage: {
      imageUrl: '/plantation_shutters/sunburstorlouvered.jpg',
      imageAlt: 'Sunburst arch versus louvered arch specialty option comparison',
    },
    specialityOptions: [
      {
        title: 'Sunburst Arch',
        description: 'Highlights the natural curve of the arch for a purely decorative effect. Works beautifully paired with rectangular operable shutters below.',
      },
      {
        title: 'Top Arch with Horizontal Louvers',
        description: 'The right choice when you want operability in the arch — great for managing light and airflow throughout the full window.',
      },
      {
        title: 'Combination',
        description: 'A sunburst arch above rectangular operable panels gives you architectural impact at the top and practical louver control below.',
      },
    ],
    motorizationImage: {
      imageUrl: '/plantation_shutters/Plantation-Shutter-Motor-Options.jpg',
      imageAlt: 'AM20 shutter motor with 16-channel remote control',
    },
    motorization: [
      {
        title: '16-Channel Remote Control',
        details: [
          'Operates up to 16 individual motors or grouped panel sets',
          'Multi-function control for flexible room configurations',
          'Can be paired with the mobile app for added convenience',
        ],
      },
      {
        title: 'AM20 Shutter Motor',
        details: [
          'High-performance motor for smooth automated panel operation',
          'App-controlled via iOS and Android',
          'Compatible with Amazon Alexa and Google Home',
          'Fits both new installations and existing shutter panels',
          'Works with wood and poly shutter materials',
          'Mounting clips included for 2½ in., 3½ in., and 4½ in. louvers',
        ],
      },
    ],
    swatches: sw('plantation'),
  },
  eclipse: {
    id: 'eclipse-shutter',
    name: 'Eclipse® Shutters',
    category: 'SHUTTERS',
    description: 'Premium poly shutters engineered to hold their finish and shape for the life of your home.',
    longDescription: 'Eclipse® shutters are built from Polyresin 3® — a material that resists fading, cracking, chipping, and warping even in humid environments. No repainting, no refinishing, no hassle.',
    keyFeatures: [
      'Polyresin 3® construction — will not fade, crack, or warp',
      'Performs in high-humidity rooms without maintenance',
      'Energy-efficient insulating properties',
      'Cord-free operation via tilt bar, rear tilt, or hidden gear system',
      'Available in three neutral color options',
    ],
    styleOptions: [
      'Louver sizes: 2½ in., 3½ in., and 4½ in.',
      'Colors: Cotton, Vanilla, and Pearl',
      'Eight decorative frame styles',
      'Standard and Deluxe divider rail options',
      'Specialty shapes: Arches, rakes, and angles',
      'French door cutouts available',
      'Bi-fold and bypass track systems with matching valances',
    ],
    operatingSystems: [
      {
        title: 'Tilt Options',
        details: [
          'Tilt Bar: Front-mounted bar ensures even louver closure with no exposed gears or staples.',
          'Rear Tilt: Hidden rear connectors give a clean profile when closed and a clear sightline when open.',
          'Hidden Gear Tilt: One-touch mechanism for smooth, precise louver depth with a completely concealed look.',
        ],
      },
      {
        title: 'Track Systems',
        details: [
          'Triple Bypass Track: Suits room dividers, wide closets, and large window openings up to 192 in. wide and 120 in. tall.',
          'Open Bypass Track: Allows panels to slide fully open for an unobstructed view while remaining operable.',
        ],
      },
      {
        title: 'Frame Options',
        imageUrl: '/eclipse_shutters/Eclipse%C2%AE-Shutters-Frame-Options.jpg',
        imageAlt: 'Eclipse shutters frame options',
        details: [
          'Available styles include Z Frame, S Frame, Trim Frame, Deluxe Trim Frame, Bullnose Frame, Casing Frame, L Frame, Casing Sill/Decorative L Frame, and Hanging Strip.',
        ],
      },
    ],
    specialityShapes: {
      imageUrl: '/eclipse_shutters/Eclipse%C2%AE-Shutters-Specialty-Options.jpg',
      imageAlt: 'Eclipse shutters specialty shapes and French door cutout options',
    },
    price: 'Contact for quote',
    images: ['/eclipse_shutters/eclipse_shutters_productpage.jpg'],
    swatches: sw('eclipse'),
  },

  // ── SHADES ──────────────────────────────────────────────────────────────────
  honeycomb: {
    id: 'honeycomb-shade',
    name: 'Honeycomb Shades',
    category: 'SHADES',
    description: 'Energy-efficient cellular shades that insulate your windows and soften incoming light.',
    longDescription: 'The signature cell structure traps air to reduce heat loss in winter and block solar gain in summer — keeping rooms comfortable while cutting energy costs year-round.',
    keyFeatures: [
      'Cellular design provides superior thermal insulation',
      'Available in single, double, and triple cell constructions',
      'Wide range of fabric opacities from sheer to blackout',
      'Multiple lift systems including top-down/bottom-up and motorized',
      'Lifetime warranty on components',
    ],
    styleOptions: [
      'Single, double, or triple cell for varying insulation levels',
      'Horizontal and vertical orientations',
      'Coordinated headrails, bottom rails, and hardware',
      'Corded, cordless, top-down/bottom-up, and motorized lift options',
    ],
    price: 'Contact for quote',
    images: ['/honeycomb_shades/honeycomb_shades_productpage.jpg'],
    swatches: sw('honeycomb'),
  },
  sheer: {
    id: 'sheer-shade',
    name: 'Sheer Shades',
    category: 'SHADES',
    description: 'Fabric shades that soften sunlight and preserve your view at the same time.',
    longDescription: 'Horizontal fabric vanes suspended between two sheer panels give you layered light control in a polished, contemporary package. Tilt the vanes to dial in exactly how much light and privacy you want.',
    keyFeatures: [
      'Horizontal vanes between sheer panels for a floating light effect',
      'Adjustable louver tilt for privacy without losing the view',
      'Available in light filtering and room darkening vane styles',
      'Manual and motorized operation options',
      'Lifetime warranty on components',
    ],
    styleOptions: [
      'Vane widths: 2 in., 2½ in., and 3 in.',
      'Light filtering and room darkening fabric options',
      'Aluminum headrail with color-coordinated fabric wrap',
      'Bottom rail with skew-adjust feature for uneven sills',
    ],
    price: 'Contact for quote',
    images: ['/sheer_shades/sheer_shades_productpage.jpg'],
    swatches: sw('sheer'),
  },
  revolve: {
    id: 'revolve-shade',
    name: 'Revolve™ Shades',
    category: 'SHADES',
    description: 'A dual-layer shade that transitions smoothly between open views, filtered light, and full privacy.',
    longDescription: 'Revolve™ shades layer a sheer fabric and a solid fabric so you can shift from an unobstructed outdoor view to complete room darkening with one simple motion — no second shade required.',
    keyFeatures: [
      'Dual-layer system transitions between view-through, filtered, and private',
      'Cordless operation for a clean, cord-free aesthetic',
      'Fade-resistant fabrics built for long-term performance',
      'Lifetime warranty on components',
    ],
    styleOptions: [
      'Cassette 100: Square cassette with color-coordinated fabric wrap',
      'Color-coordinated aluminum bottom rail',
      'Fabric collection: 16 designer patterns — 14 light filtering, 2 room darkening',
    ],
    price: 'Contact for quote',
    images: ['/revolve_shades/revolve_shades_productpage.jpg'],
    swatches: sw('revolve'),
  },
  roller: {
    id: 'roller-shade',
    name: 'Roller Shades',
    category: 'SHADES',
    description: 'Clean-lined, minimal shades available in a wide range of fabrics to suit any interior.',
    longDescription: 'Roller shades pair a simple, timeless mechanism with an extensive fabric library spanning sheer solar screens to full blackout. They work in every room and complement every style.',
    keyFeatures: [
      'Fabric options from sheer to blackout for any light preference',
      'UV-filtering solar screens reduce glare and protect furnishings',
      'Dual and coupled shade configurations available',
      'Safety-certified and GREENGUARD® and Oeko-Tex® options',
      'Lifetime warranty on components',
    ],
    styleOptions: [
      'Cassette, fascia, and open-roll headrail systems',
      'Multiple bottom rail profiles',
      'Inside and outside mount options',
      'Regular and reverse roll configurations',
      'Single, dual, and coupled shade setups',
    ],
    price: 'Contact for quote',
    images: ['/roller_shades/roller_shades_productpage.jpg'],
    swatches: sw('roller'),
  },
  outdoor: {
    id: 'outdoor-shade',
    name: 'Outdoor Shades',
    category: 'SHADES',
    description: 'Sun and weather protection for patios, pergolas, and decks — built to last outdoors.',
    longDescription: 'Outdoor shades use performance fabrics engineered to handle UV exposure, moisture, and wind without fading or degrading. Available as retractable solar screens, roller shades, and zipper track systems.',
    keyFeatures: [
      'Performance fabrics rated for prolonged sun, wind, and moisture exposure',
      'Resists mildew, fading, and weathering',
      'Compatible with manual and motorized operation',
      'Suited for residential patios and commercial exteriors',
      'Lifetime warranty on components',
    ],
    styleOptions: [
      'Fabric lines: Natté™, Satiné™, NordicScreen, and E-Screen',
      'Color palettes: Charcoal, Grey, and Sable',
      'Retractable solar screen, roller, and zipper track system options',
    ],
    price: 'Contact for quote',
    images: ['/outdoor_shades/outdoor_shades_productpage.jpg'],
    swatches: sw('outdoor'),
  },
};

export const getProductById = (id) => {
  return Object.values(productData).find((p) => p.id === id) || null;
};

export const getProductsByCategory = (category) => {
  return Object.values(productData).filter((p) => p.category === category);
};